/**
 * NotificationTriggerService — Client-side service for triggering event-based notifications
 *
 * Calls /api/send-event-notification for:
 * - rescheduled: appointment date/time changed
 * - no-show: patient marked as no-show
 * - post-session: session completed
 * - balance-reminder: outstanding balance notification
 */

const API_URL = '/api/send-event-notification';

interface NotificationPayload {
  type: 'rescheduled' | 'no-show' | 'post-session' | 'balance-reminder';
  patientId: string;
  providerId?: string;
  data?: Record<string, any>;
}

interface NotificationResult {
  status: 'sent' | 'failed' | 'skipped';
  channels?: string[];
  reason?: string;
}

class NotificationTriggerService {
  private static instance: NotificationTriggerService;

  static getInstance(): NotificationTriggerService {
    if (!this.instance) this.instance = new NotificationTriggerService();
    return this.instance;
  }

  private async send(payload: NotificationPayload): Promise<NotificationResult> {
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        console.error('[NotificationTrigger] API error:', res.status);
        return { status: 'failed', reason: `HTTP ${res.status}` };
      }
      return await res.json();
    } catch (err: any) {
      console.error('[NotificationTrigger] Network error:', err.message);
      return { status: 'failed', reason: err.message };
    }
  }

  /**
   * Send rescheduled appointment notification
   */
  async sendRescheduled(params: {
    patientId: string;
    providerId: string;
    oldDate: string;
    oldTime: string;
    newDate: string;
    newTime: string;
    modality?: string;
    appointmentId?: string;
  }): Promise<NotificationResult> {
    return this.send({
      type: 'rescheduled',
      patientId: params.patientId,
      providerId: params.providerId,
      data: {
        oldDate: params.oldDate,
        oldTime: params.oldTime,
        newDate: params.newDate,
        newTime: params.newTime,
        modality: params.modality || 'in-person',
        appointmentId: params.appointmentId,
      },
    });
  }

  /**
   * Send no-show follow-up notification
   */
  async sendNoShow(params: {
    patientId: string;
    providerId: string;
    appointmentDate: string;
    startTime: string;
    appointmentId?: string;
  }): Promise<NotificationResult> {
    return this.send({
      type: 'no-show',
      patientId: params.patientId,
      providerId: params.providerId,
      data: {
        appointmentDate: params.appointmentDate,
        startTime: params.startTime,
        appointmentId: params.appointmentId,
      },
    });
  }

  /**
   * Send post-session follow-up
   */
  async sendPostSession(params: {
    patientId: string;
    providerId: string;
    appointmentDate: string;
    appointmentId?: string;
  }): Promise<NotificationResult> {
    return this.send({
      type: 'post-session',
      patientId: params.patientId,
      providerId: params.providerId,
      data: {
        appointmentDate: params.appointmentDate,
        appointmentId: params.appointmentId,
      },
    });
  }

  /**
   * Send balance reminder
   */
  async sendBalanceReminder(params: {
    patientId: string;
    providerId?: string;
    balance: number;
  }): Promise<NotificationResult> {
    return this.send({
      type: 'balance-reminder',
      patientId: params.patientId,
      providerId: params.providerId,
      data: {
        balance: params.balance,
      },
    });
  }
}

export default NotificationTriggerService;
